/**
 * MultiSelect: themed multi-select dropdown (analytics filter bar, services
 * catalog filter, the admin "Start a service" pickers).
 *
 * FancySelect's sibling: same trigger/panel design language, but with
 * checkbox-style options, a "Select all" row, and a search box. The value is a
 * comma-separated string ("a,b,c"; empty = All) so it slots straight into the
 * URL-search-param filter state and the backend's CSV filter params.
 *
 * The panel is portaled to <body> for the same reasons as FancySelect: a Dialog
 * is overflow-y-auto and z-50, so an absolutely positioned panel would be
 * clipped by the modal and could not sit above it.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '../lib/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
}

/** Fixed coords for the portaled panel (top OR bottom, never both). */
interface PanelPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

export function MultiSelect({
  options,
  value,
  onChange,
  allLabel,
  ariaLabel,
  searchable,
}: {
  options: MultiSelectOption[];
  /** Comma-separated selected values; '' means "All". */
  value: string;
  onChange: (csv: string) => void;
  /** Trigger label when nothing is selected, e.g. "All months". */
  allLabel: string;
  ariaLabel?: string;
  /** Force the search box on/off. Omit to show it automatically for long lists. */
  searchable?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [pos, setPos] = React.useState<PanelPos | null>(null);

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const showSearch = searchable ?? options.length > 6;

  const selected = React.useMemo(
    () => new Set(value ? value.split(',').filter(Boolean) : []),
    [value],
  );

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const commit = (next: Set<string>) => {
    // Keep the CSV in option order so identical selections produce identical
    // URLs and query keys.
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value).join(','));
  };

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    commit(next);
  };

  // "Select all" applies to the currently visible (searched) options.
  const allVisibleSelected = visible.length > 0 && visible.every((o) => selected.has(o.value));
  const toggleAllVisible = () => {
    const next = new Set(selected);
    if (allVisibleSelected) visible.forEach((o) => next.delete(o.value));
    else visible.forEach((o) => next.add(o.value));
    commit(next);
  };

  /** Place the fixed-position panel against the trigger, flipping up if needed. */
  const measure = React.useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 6;
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    const up = below < 220 && above > below;
    setPos({
      left: rect.left,
      width: rect.width,
      ...(up ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      maxHeight: Math.max(160, Math.min(280, up ? above : below)),
    });
  }, []);

  React.useEffect(() => {
    if (!open) {
      setPos(null);
      setQuery('');
      return;
    }
    measure();
    // The filter box lives in the trigger (inside any Dialog), so focusing it
    // does not fight the dialog's focus trap the way the portaled panel would.
    if (showSearch) {
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }
  }, [open, measure, showSearch]);

  // A modal Dialog's scroll lock (react-remove-scroll) listens for wheel on
  // document and preventDefaults anything outside the dialog, which would stop
  // this body-portaled panel from scrolling. Its listener is bubble-phase, so
  // stopping the event at the panel keeps native scrolling working.
  const panelMounted = pos !== null;
  React.useEffect(() => {
    const el = panelRef.current;
    if (!open || !el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: false });
    el.addEventListener('touchmove', stop, { passive: false });
    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, [open, panelMounted]);

  // Close on outside click / Escape; follow the trigger on scroll and resize.
  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (
        anchorRef.current && !anchorRef.current.contains(t) &&
        panelRef.current && !panelRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onReposition(e: Event) {
      // Scrolling the panel itself must not move it. Any other scroll just
      // re-anchors it; closing here would make the list feel unscrollable.
      const t = e.target as Node | null;
      if (t && panelRef.current && panelRef.current.contains(t)) return;
      measure();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, measure]);

  const triggerLabel =
    selected.size === 0
      ? allLabel
      : selected.size === 1
        ? (options.find((o) => selected.has(o.value))?.label ?? `${selected.size} selected`)
        : `${selected.size} selected`;

  const boxClass = cn(
    'w-full inline-flex items-center justify-between gap-2 rounded-lg border bg-background',
    'px-3 py-2.5 text-sm font-medium text-foreground transition-colors border-border',
  );

  return (
    <div ref={anchorRef} className="relative">
      {open && showSearch ? (
        <div className={cn(boxClass, 'ring-2 ring-ring')}>
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded
            aria-label={ariaLabel ?? allLabel}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={triggerLabel}
            className="min-w-0 flex-1 bg-transparent text-left outline-none placeholder:text-muted-foreground"
          />
          <ChevronDown className="h-4 w-4 shrink-0 rotate-180 opacity-60" aria-hidden />
        </div>
      ) : (
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? allLabel}
          onClick={() => setOpen((o) => !o)}
          className={cn(boxClass, 'hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring')}
        >
          <span className="truncate">{triggerLabel}</span>
          <span className="flex items-center gap-1.5">
            {selected.size > 0 && (
              <span className="rounded-full bg-accent/15 px-1.5 text-[10px] font-bold tabular-nums text-accent">
                {selected.size}
              </span>
            )}
            <ChevronDown className={cn('h-4 w-4 opacity-60 transition-transform', open && 'rotate-180')} />
          </span>
        </button>
      )}

      {open && pos && createPortal(
        <div
          ref={panelRef}
          // A modal Radix Dialog sets pointer-events:none on <body> and
          // dismisses on outside pointerdown, so keep this layer clickable and
          // stop the event before it reaches the dialog's document listener.
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
          className={cn(
            // Portaled to <body> above the Dialog (z-50) so an overflow-y-auto
            // modal cannot clip it.
            'z-[60] flex flex-col pointer-events-auto',
            'origin-top animate-slide-up-fade rounded-xl border border-border bg-background p-1.5 shadow-lg',
          )}
        >
          <ul role="listbox" aria-multiselectable className="flex-1 overflow-y-auto overscroll-contain">
            {visible.length > 1 && (
              <li>
                <button
                  type="button"
                  onClick={toggleAllVisible}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <CheckBox checked={allVisibleSelected} />
                  Select all{query.trim() ? ' (matching)' : ''}
                </button>
              </li>
            )}
            {visible.length === 0 && (
              <li className="px-2.5 py-3 text-center text-xs text-muted-foreground">
                No matches
              </li>
            )}
            {visible.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                      checked ? 'text-foreground' : 'text-foreground/90',
                      'hover:bg-muted',
                    )}
                  >
                    <CheckBox checked={checked} />
                    <span className="truncate">{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Always present: selecting keeps the panel open (that is the point of
              a multi-select), so there has to be a deliberate way to close it. */}
          <div className="mt-1 flex shrink-0 items-center justify-between gap-2 border-t border-border pt-1">
            {selected.size > 0 ? (
              <button
                type="button"
                onClick={() => onChange('')}
                className="truncate rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-accent transition-colors hover:bg-muted"
              >
                Clear selection ({selected.size})
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Done
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background',
      )}
    >
      {checked && <Check className="h-3 w-3" />}
    </span>
  );
}
